import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  getDownloadURL,
  ref,
  Storage,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { AccountingService } from '../../../services/accounting.service';
import { AuthService } from '../../../services/auth.service';
import { UtilsService } from '../../../services/utils.service';
import * as groupActions from '../../../state/actions/group.actions';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { AlertType } from '../../components/dialogs/confirm.dialog.model';
import { DialogService } from '../../components/dialogs/dialog.service';
import { CreateExpenseReportComponent } from './create-expense-report/create-expense-report.component';
import { FundingComponent } from '../funding/funding.component';

@Component({
  selector: 'app-accounting',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnActionComponent,
    RouterLink,
    CreateExpenseReportComponent,
    FundingComponent,
  ],
  templateUrl: './accounting.component.html',
})
export class AccountingComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private store: Store<AppState> = inject(Store<AppState>);
  private auth: AuthService = inject(AuthService);
  private accountingService: AccountingService = inject(AccountingService);
  private toastr: ToastrService = inject(ToastrService);
  private storage: Storage = inject(Storage);
  private router: Router = inject(Router);
  private dialogService: DialogService = inject(DialogService);
  private logger: NGXLogger = inject(NGXLogger);
  private utilsService: UtilsService = inject(UtilsService);

  groupId: string | null = null;
  userId: string | null = null;
  user: any = null;
  isAdmin = false;
  isTreasurer = false;
  showLedgerModal = false;
  showTitheComingSoonModal = false;
  group: any = null;

  activeTab: 'ledger' | 'summary' | 'funding' | 'reports' = 'summary';

  showCreateReport = false;
  editingReport: any = null;
  expenseReports: any[] = [];

  // Ledger List & Filter States
  ledgerEntries: any[] = [];
  filteredEntries: any[] = [];
  months = [
    { value: '', label: 'All Time' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  selectedMonth = '';
  selectedYear = '';
  searchQuery = '';
  isSearching = false;

  tempMonth = '';
  tempYear = '';
  tempSearchQuery = '';

  // Summary Metrics
  totalGroupIncome = 0;
  totalGroupExpenses = 0;
  netGroupBalance = 0;
  isRecalculating = false;
  lastUpdated: any = null;

  // Forms
  ledgerForm: FormGroup;

  isSubmittingLedger = false;
  isScanningReceipt = false;

  private exit$ = new Subject<void>();

  constructor() {
    this.groupId = this.route.snapshot.paramMap.get('id');
    this.userId = this.auth.getUid() || null;

    // Set default selected year to empty on load, but prepopulate tempYear for the input
    this.selectedYear = '';
    this.tempYear = new Date().getFullYear().toString();

    this.ledgerForm = new FormGroup({
      amount: new FormControl('', [Validators.required, Validators.min(0.01)]),
      currency: new FormControl('usd', [Validators.required]),
      conversionRate: new FormControl(1, [
        Validators.required,
        Validators.min(0.000001),
      ]),
      date: new FormControl(new Date().toISOString().substring(0, 10), [
        Validators.required,
      ]),
      description: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
      ]),
      type: new FormControl('income', [Validators.required]),
      payerName: new FormControl(''),
      tags: new FormControl(''),
      category: new FormControl('tithe'),
    });



    this.store
      .select((state: any) => state.user?.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.route.queryParams.pipe(takeUntil(this.exit$)).subscribe((params) => {
      const tab = params['tab'];
      if (
        tab === 'ledger' ||
        tab === 'summary' ||
        tab === 'funding' ||
        tab === 'reports'
      ) {
        this.activeTab = tab;
      }
    });
  }

  ngOnInit(): void {
    if (!this.groupId) {
      this.toastr.error('Group ID not found');
      return;
    }

    this.store
      .select(groupSelector.getExtractedReceipt)
      .pipe(takeUntil(this.exit$))
      .subscribe((extraction) => {
        if (extraction) {
          if (extraction.error) {
            // Ignore error if it contains a receiptId (meant for single/batch scans)
            if (extraction.receiptId) {
              return;
            }
            this.utilsService.onShowAlert(
              {
                title: 'Receipt Scan Failed',
                message:
                  extraction.errorMessage ||
                  'AI was unable to extract details from this receipt.',
              } as AppExceptionModel,
              false,
            );
            this.store.dispatch(groupActions.clearExtractedReceipt());
            return;
          }

          // Ignore success if it contains a receiptId (meant for single/batch scans)
          if (extraction.receiptId) {
            return;
          }

          this.ledgerForm.patchValue({
            amount: extraction.amount,
            currency: extraction.currency?.toLowerCase() || 'usd',
            date: extraction.date
              ? extraction.date.substring(0, 10)
              : new Date().toISOString().substring(0, 10),
            description: extraction.description,
            type: 'expense',
          });
          this.toastr.success(
            'Successfully extracted receipt details!',
            'Extraction Success',
          );
          this.store.dispatch(groupActions.clearExtractedReceipt());
        }
      });

    this.store
      .select(groupSelector.getExtractingReceipt)
      .pipe(takeUntil(this.exit$))
      .subscribe((isScanning) => {
        this.isScanningReceipt = isScanning;
      });

    // Combine Roles, Ledger entries, expense reports, and precalculated financial summary
    combineLatest([
      this.store.select(groupSelector.getMyRolesInGroup(this.groupId)),
      this.accountingService.getLedger(this.groupId),
      this.store.select(groupSelector.getGroupById(this.groupId)),
      this.accountingService.getExpenseReports(this.groupId),
      this.accountingService.getFinancialSummary(this.groupId),
    ])
      .pipe(takeUntil(this.exit$))
      .subscribe(([roles, ledger, group, reports, summary]) => {
        if (roles) {
          this.isAdmin = roles.admin === true;
          this.isTreasurer = roles.treasurer === true;
        }

        if (ledger) {
          this.ledgerEntries = ledger;
          this.applyFilters();
        }

        if (group) {
          this.group = group;
          // Set default form currency to group default currency if form hasn't been touched
          if (group.currency && !this.ledgerForm.get('currency')?.dirty) {
            this.ledgerForm.patchValue({
              currency: group.currency.toLowerCase(),
            });
          }
        }
        if (reports) {
          this.expenseReports = reports;
        }

        if (summary) {
          this.totalGroupIncome = summary.totalIncome || 0;
          this.totalGroupExpenses = summary.totalExpenses || 0;
          this.netGroupBalance = summary.netBalance || 0;
          this.lastUpdated = summary.lastUpdated;
        } else {
          this.totalGroupIncome = 0;
          this.totalGroupExpenses = 0;
          this.netGroupBalance = 0;
          this.lastUpdated = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }
  async recalculateSummary() {
    if (!this.groupId) return;
    if (this.isRecalculating) return;
    this.isRecalculating = true;
    this.logger.info(
      `Starting financial summary sync for group: ${this.groupId}`,
    );
    try {
      await this.accountingService.recalculateFinancialSummary(this.groupId);
      this.toastr.success(
        'Financial summary synced successfully!',
        'Sync Success',
      );
      this.logger.info(
        `Financial summary synced successfully for group: ${this.groupId}`,
      );
    } catch (err: any) {
      this.logger.error('Failed to sync financial summary:', err);
      this.dialogService.alert({
        title: 'Sync Failed',
        description:
          err.message ||
          'An unexpected error occurred while syncing the financial data.',
        showType: AlertType.error,
        action: 'Close',
        buttonColor: 'bg-red-600',
        buttonTextColor: 'text-white',
      });
    } finally {
      this.isRecalculating = false;
    }
  }
  getLastUpdatedDate(): Date | null {
    if (!this.lastUpdated) return null;
    return this.lastUpdated.seconds
      ? new Date(this.lastUpdated.seconds * 1000)
      : new Date(this.lastUpdated);
  }

  applyFilters() {
    if (!this.isSearching) {
      // On initial load or when search is cleared, show the last 10 expenses/income records
      this.filteredEntries = this.ledgerEntries.slice(0, 10);
      return;
    }

    // When searching, display only searched results
    this.filteredEntries = this.ledgerEntries.filter((entry) => {
      const entryDate = this.getEntryDate(entry);
      const monthMatches =
        !this.selectedMonth ||
        (entryDate.getMonth() + 1).toString().padStart(2, '0') ===
          this.selectedMonth;

      const yearMatches =
        !this.selectedYear ||
        entryDate.getFullYear().toString() === this.selectedYear;

      const tagsMatches =
        entry.tags &&
        Array.isArray(entry.tags) &&
        entry.tags.some((t: string) =>
          t.toLowerCase().includes(this.searchQuery.toLowerCase()),
        );

      const searchMatches =
        !this.searchQuery ||
        entry.description
          ?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        entry.payerName
          ?.toLowerCase()
          .includes(this.searchQuery.toLowerCase()) ||
        entry.paidTo?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        entry.type?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tagsMatches;

      return monthMatches && yearMatches && searchMatches;
    });
  }

  executeSearch() {
    this.searchQuery = this.tempSearchQuery;
    this.selectedMonth = this.tempMonth;
    this.selectedYear = this.tempYear;
    this.isSearching = true;
    this.applyFilters();
  }

  clearSearch() {
    this.tempSearchQuery = '';
    this.tempMonth = '';
    this.tempYear = '';
    this.searchQuery = '';
    this.selectedMonth = '';
    this.selectedYear = '';
    this.isSearching = false;
    this.applyFilters();
  }

  getEntryDate(entry: any): Date {
    if (!entry?.date) return new Date();
    return entry.date.seconds
      ? new Date(entry.date.seconds * 1000)
      : new Date(entry.date);
  }

  async saveLedger() {
    if (this.ledgerForm.invalid || !this.groupId) return;
    this.isSubmittingLedger = true;

    try {
      const tagsArray = this.ledgerForm.value.tags
        ? this.ledgerForm.value.tags
            .split(',')
            .map((t: string) => t.trim().toLowerCase())
            .filter(Boolean)
        : [];

      const entry: any = {
        amount: parseFloat(this.ledgerForm.value.amount),
        currency: this.ledgerForm.value.currency,
        conversionRate:
          this.ledgerForm.value.currency !==
          (this.group?.currency || 'USD').toLowerCase()
            ? parseFloat(this.ledgerForm.value.conversionRate || 1)
            : 1,
        date: new Date(this.ledgerForm.value.date),
        description: this.ledgerForm.value.description,
        type: this.ledgerForm.value.type,
        payerName: this.ledgerForm.value.payerName || '',
        recordedBy: this.userId,
        tags: tagsArray,
      };

      if (this.ledgerForm.value.type === 'income') {
        entry.category = this.ledgerForm.value.category || 'tithe';
      }

      await this.accountingService.addLedgerEntry(this.groupId, entry);
      this.toastr.success('Ledger entry added successfully');

      this.ledgerForm.reset({
        amount: '',
        currency: this.group?.currency?.toLowerCase() || 'usd',
        conversionRate: 1,
        date: new Date().toISOString().substring(0, 10),
        description: '',
        type: 'income',
        payerName: '',
        tags: '',
        category: 'tithe',
      });
      this.showLedgerModal = false;
      this.isSubmittingLedger = false;
    } catch (error: any) {
      this.toastr.error(error.message || 'Failed to add ledger entry');
      this.isSubmittingLedger = false;
    }
  }



  async handleReceiptUpload(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    // Validate size and type
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.error('Receipt image size exceeds 5MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.toastr.error(
        'Invalid image type. Only JPG, JPEG, and PNG are allowed.',
      );
      return;
    }

    const isAvailable = await firstValueFrom(
      this.store.select(groupSelector.isAiCreditsAvailable),
    );
    if (!isAvailable) {
      const buyNow = await this.dialogService.confirm({
        title: 'Out of AI Credits',
        description:
          'Your group has run out of AI credits. Please subscribe or buy more credits to continue using the AI receipt scanner.',
        action: 'Buy Now',
        cancelText: 'Buy Later',
      });
      if (buyNow && this.groupId) {
        this.router.navigate([
          '/school',
          this.groupId,
          'billing',
          'addonsubscribe',
        ]);
      }
      event.target.value = '';
      return;
    }

    this.isScanningReceipt = true;
    this.toastr.info(
      'AI is scanning your receipt, please wait...',
      'Receipt Reader',
    );

    try {
      const extension = file.name.split('.').pop() || 'jpeg';
      const fileId = `${this.userId || 'anonymous'}_${Date.now()}.${extension}`;
      const storagePath = `groups/${this.groupId}/receipts/quick_scans/${fileId}`;
      const storageRef = ref(this.storage, storagePath);
      const metadata = { contentType: file.type };
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          () => resolve(),
        );
      });

      const gcsPath = `gs://${storageRef.bucket}/${storageRef.fullPath}`;

      if (this.groupId) {
        this.store.dispatch(
          groupActions.extractReceipt({
            groupId: this.groupId,
            gcsPath: gcsPath,
            mimeType: file.type,
          }),
        );
      }
    } catch (error: any) {
      this.toastr.error(
        error.message ||
          'AI failed to parse the receipt. You can still input details manually.',
      );
      this.isScanningReceipt = false;
    } finally {
      // Clear file input
      event.target.value = '';
    }
  }

  openCreateExpenseReport(report?: any) {
    this.showCreateReport = true;
    this.editingReport = report || null;
  }

  handleReportSave() {
    this.showCreateReport = false;
    this.editingReport = null;
    this.activeTab = 'reports';
  }

  async publishReport(report: any) {
    if (!this.groupId || !report.id || !report.expenses) return;
    try {
      await this.accountingService.publishExpenseReport(
        this.groupId,
        report.id,
        report.expenses,
      );
      this.toastr.success('Expense report published to ledger successfully.');
    } catch (err: any) {
      this.toastr.error(err.message || 'Failed to publish expense report');
    }
  }

  async enableTithe() {
    this.showTitheComingSoonModal = true;
  }

  getReportDate(report: any): Date {
    if (!report?.created) return new Date();
    return report.created.seconds
      ? new Date(report.created.seconds * 1000)
      : new Date(report.created);
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }
}
